import React, { useState } from "react";
import * as Yup from "yup";
import "./SignIn.css";
import { Link } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Card,
  IconButton,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Field, Form, Formik } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { newPassword } from "./Apihelper/Profile";
import AppToast from "../components/Toast/AppToast";

const validationSchema = Yup.object({
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Confirm password is required"),
});

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(location.search);
  const tokenFromUrl = urlParams.get("token");

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(false);
    if (!tokenFromUrl) {
      setSnackbarMessage("Invalid or missing reset token. Please request a new link.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setLoading(true);
    try {
      const data = await newPassword(values.newPassword, tokenFromUrl);
      if (data.code === "200" || data.code === 200) {
        setSnackbarMessage("Password changed successfully! Redirecting to login...");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        navigate("/");
      } else {
        setSnackbarMessage(data.message || "Failed to reset password. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    marginBottom: "20px",
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": { borderColor: "#1B4332" },
      "&.Mui-focused fieldset": { borderColor: "#1B4332" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#1B4332" },
  };

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        margin: 0,
        padding: 0,
        background: "linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "fixed",
        top: 0,
        left: 0,
        overflow: "hidden",
      }}
    >
      <Container maxWidth="sm">
        <Card
          elevation={8}
          sx={{
            padding: "40px",
            borderRadius: "16px",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box textAlign="center" mb={4}>
            <Typography
              variant="h5"
              component="p"
              sx={{ color: "#1B4332", fontWeight: 700, marginBottom: "4px" }}
            >
              Maa Tara Tarini Multi gym
            </Typography>
            <Typography
              variant="h4"
              component="h1"
              sx={{ color: "#1B4332", fontWeight: 600, marginBottom: "8px" }}
            >
              Set New Password
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#666", maxWidth: "400px", margin: "0 auto", lineHeight: 1.5 }}
            >
              Enter 8–256 characters. Combine uppercase, lowercase, numbers, and symbols.
            </Typography>
            {!tokenFromUrl && (
              <Typography variant="body2" sx={{ color: "red", mt: 1, fontWeight: 500 }}>
                No reset token found. Please use the link from your email.
              </Typography>
            )}
          </Box>

          <Formik
            initialValues={{ newPassword: "", confirmPassword: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form>
                <Field
                  as={TextField}
                  name="newPassword"
                  fullWidth
                  label="New Password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  error={touched.newPassword && Boolean(errors.newPassword)}
                  helperText={touched.newPassword && errors.newPassword}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: "#1B4332" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((p) => !p)} edge="end">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Field
                  as={TextField}
                  name="confirmPassword"
                  fullWidth
                  label="Confirm Password"
                  variant="outlined"
                  type={showConfirmPassword ? "text" : "password"}
                  error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: "#1B4332" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword((p) => !p)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  disabled={loading || !tokenFromUrl}
                  sx={{
                    background: "linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)",
                    color: "white",
                    padding: "12px",
                    borderRadius: "8px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    textTransform: "none",
                    boxShadow: "0 4px 12px rgba(26, 35, 126, 0.2)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #2D6A4F 0%, #1B4332 100%)",
                      boxShadow: "0 6px 16px rgba(26, 35, 126, 0.3)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    "Reset Password"
                  )}
                </Button>

                <Box sx={{ textAlign: "center", marginTop: "24px" }}>
                  <Link
                    to="/"
                    style={{ color: "#1B4332", textDecoration: "none", fontWeight: 500 }}
                  >
                    Back to Login
                  </Link>
                </Box>
              </Form>
            )}
          </Formik>
        </Card>
      </Container>

      <AppToast
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default ResetPassword;
